import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  INDIVIDUAL_ASSISTANT_REFERENCE,
  INDIVIDUAL_CONVERSATION_ORIGIN,
  UOU_ASSISTANT_REFERENCE,
  UOU_CONVERSATION_ORIGIN,
} from '@wix/ai-scheduling-assistant-constants';
import { useContactCalendarOAuth } from '@wix/ai-scheduling-assistant-data-store';
import { useChatServiceProvider } from '@wix/chat-service-sdk';
import { ChatMessageData } from '@wix/chat-widget-ui-lib';
import { useAuthTokenContext } from '../../../context/AuthTokenContext';
import { useHttpClientsContext } from '../../../context/HttpClientsContext';
import { useScreenWidth } from '../../../hooks/useScreenWidth';

export const useUoUChatService = (useIndividualAssistant: boolean, onMessageSent: () => void) => {
  const { chatHttpClient, chatClient } = useHttpClientsContext();
  const { decodedAuthToken, setAuthToken } = useAuthTokenContext();
  const { isMobile } = useScreenWidth();
  const [conversationId, setConversationId] = useState<string | null>(decodedAuthToken?.conversationId || null);
  const { authToken } = useAuthTokenContext();
  const { refetchConnectionStatus: refetchConnectionStatusBase } = useContactCalendarOAuth(authToken);

  const {
    isLoading,
    messages,
    loadConversation,
    createConversation,
    sendUserMessage: sendUserMessageSDK,
    quickReplySuggestions,
  } = useChatServiceProvider({
    enablePolling: true,
    httpClient: chatHttpClient,
    reference: useIndividualAssistant ? INDIVIDUAL_ASSISTANT_REFERENCE : UOU_ASSISTANT_REFERENCE,
    instanceIdForSideEffects: window.__APP_SIGNED_INSTANCE__,
  });

  const loadOrCreateConversation = useCallback(async () => {
    let conversationIdToSet;
    if (conversationId) {
      conversationIdToSet = await loadConversation({ id: conversationId });
    } else {
      conversationIdToSet = await createConversation({ origin: useIndividualAssistant ? INDIVIDUAL_CONVERSATION_ORIGIN : UOU_CONVERSATION_ORIGIN });
    }
    setConversationId(conversationIdToSet);
  }, [conversationId, loadConversation, createConversation, useIndividualAssistant]);

  const handleConversationToken = useCallback(async () => {
    if (!decodedAuthToken && conversationId) {
      const response = await chatClient.getConversationToken(conversationId);
      if (response.token) {
        setAuthToken(response.token);
      }
    }
  }, [chatClient, conversationId, decodedAuthToken, setAuthToken]);

  const sendUserMessage = useCallback(
    async (message: ChatMessageData) => {
      onMessageSent();

      if (isMobile) {
        setTimeout(() => (document.activeElement as HTMLElement)?.blur(), 100);
      }

      await sendUserMessageSDK(message.content);
    },
    [onMessageSent, isMobile, sendUserMessageSDK],
  );

  const onRetry = useCallback(async () => {
    const { isError } = await chatClient.isConversationError(conversationId!);
    if (isError) {
      sendUserMessage({ content: 'Please try again', role: 'user' });
    } else {
      loadOrCreateConversation();
    }
  }, [chatClient, conversationId, sendUserMessage, loadOrCreateConversation]);

  const refetchConnectionStatus = useCallback(async () => {
    await refetchConnectionStatusBase();
  }, [refetchConnectionStatusBase]);

  const tags = useMemo(() => {
    return quickReplySuggestions.length > 0
      ? quickReplySuggestions.map((suggestion) => ({
          text: suggestion.label,
          onClick: () => sendUserMessage({ content: suggestion.reply, role: 'user' }),
        }))
      : undefined;
  }, [quickReplySuggestions, sendUserMessage]);

  useEffect(() => {
    if (conversationId) {
      handleConversationToken();
    }
  }, [conversationId, handleConversationToken]);

  return {
    isLoading,
    messages,
    tags,
    sendUserMessage,
    loadOrCreateConversation,
    onRetry,
    refetchConnectionStatus,
  };
};
